import React, { useState } from "react";
import { ArrowLeft, Send, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const inputCls =
  "w-full bg-[#FFF9F2] border border-[#FCE6E9] rounded-2xl px-4 py-2.5 text-sm text-[#374151] placeholder:text-[#9CA3AF] outline-none focus:ring-2 focus:ring-[#B3E0F2] focus:border-transparent transition-all";
const labelCls = "text-xs font-semibold uppercase tracking-wider text-[#9CA3AF] block mb-1.5";

// Contact page — inquiry form (validated, rate-limited, stored + emailed to the admin queue)
export default function ContactPage({ onBack }) {
  const [form, setForm] = useState({ name: "", email: "", subject: "", category: "Support", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === "string" ? body.detail : "Send failed");
      setSent(true);
      toast.success("Message sent — our team will reply by email shortly.");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFF8E7] dark:bg-[#1F2937]" data-testid="contact-page">
      <header className="max-w-3xl mx-auto px-6 pt-8 flex items-center justify-between">
        <button onClick={onBack} className="flex items-center gap-2.5 cursor-pointer" data-testid="contact-brand" title="Back to home">
          <img src="/assets/logo.png" alt="NestTrack" className="w-10 h-10 rounded-full" />
          <span className="text-xl font-black font-heading text-[#374151] dark:text-slate-100">NestTrack</span>
        </button>
        <button onClick={onBack} data-testid="contact-back-btn" className="flex items-center gap-1.5 px-4 py-2 bg-white border border-[#FCE6E9] text-[#6B7280] rounded-full text-xs font-bold hover:border-[#F8C8DC] transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Home
        </button>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-14">
        <div className="max-w-xl mx-auto">
          <span className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-[#E6F2FA] text-[#3D7E9E] text-[10px] font-extrabold uppercase tracking-widest rounded-full mb-5">
            <Mail className="w-3 h-3" /> We reply within one business day
          </span>
          <h1 className="text-4xl font-black font-heading text-[#374151] dark:text-slate-100 mb-3">Send us a message</h1>
          <p className="text-sm text-[#6B7280] dark:text-slate-300 mb-8">Support tickets, store questions, or general business inquiries — everything lands in our admin queue and triggers an email notification.</p>

          {sent ? (
            <div className="p-10 bg-white border border-[#C4E5C2] rounded-[2rem] text-center dark:bg-[#232D3D] dark:border-[#2F6031]" data-testid="contact-success">
              <CheckCircle2 className="w-12 h-12 text-[#2F6031] mx-auto mb-4" />
              <h2 className="text-xl font-black font-heading text-[#374151] mb-2">Message received</h2>
              <p className="text-sm text-[#6B7280] mb-6">Your inquiry is in the support queue. A confirmation has been emailed to our admin team.</p>
              <button onClick={onBack} className="px-6 py-2.5 bg-[#F8C8DC] hover:bg-[#EAAFC5] text-[#374151] rounded-full text-sm font-bold transition-all" data-testid="contact-done-btn">
                Back to Home
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="p-7 bg-white border border-[#FCE6E9] rounded-[2rem] shadow-sm space-y-4 dark:bg-[#232D3D] dark:border-[#374457]" data-testid="contact-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Name</label>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="contact-name-input" />
                </div>
                <div>
                  <label className={labelCls}>Email</label>
                  <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="contact-email-input" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Subject</label>
                  <input required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className={inputCls} data-testid="contact-subject-input" />
                </div>
                <div>
                  <label className={labelCls}>Category</label>
                  <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} data-testid="contact-category-select">
                    <option value="Support">Support</option>
                    <option value="Store">Store</option>
                    <option value="General">General</option>
                  </select>
                </div>
              </div>
              <div>
                <label className={labelCls}>Message</label>
                <textarea required value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className={`${inputCls} min-h-[130px]`} placeholder="How can we help?" data-testid="contact-message-input" />
              </div>
              <button type="submit" disabled={sending} className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-[#374151] hover:bg-[#2b3441] text-white rounded-full text-sm font-bold transition-all disabled:opacity-50" data-testid="contact-submit-btn">
                <Send className="w-4 h-4" /> {sending ? "Sending…" : "Send Message"}
              </button>
              <p className="text-[10px] text-[#9CA3AF] text-center font-medium">Protected by anti-spam rate limiting (3 messages/hour).</p>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}
